import { FirestoreDataConverter } from '@angular/fire/firestore';
import { QuotationState } from '../enums/quotation-state.enum';
import { Notification } from '../enums/notification.enum';
import { ProductOrder } from "./product-order.model";
import { ObjectHelper } from "../helpers/object.helper";
import { SourceType } from "../enums/source-type.enum";
import { FormControl, FormGroup } from "@angular/forms";
import { DeliveryAddress } from "./delivery-address.model";
import { ProductionModeEnum } from "../enums/production-mode.enum";
import { DocumentModel } from "./document.model";

export type QuotationForm = FormGroup<{
  id: FormControl<string>;
  erpId: FormControl<string>;
  version: FormControl<number>;
  organizationId: FormControl<string>;
  description: FormControl<string>;
  reference: FormControl<string>;
  email: FormControl<string>;
  contactName: FormControl<string>;
  company: FormControl<string>;
  state: FormControl<QuotationState>;
  customerId: FormControl<string>;
  date: FormControl<Date>;
  emailSent: FormControl<Notification>;
  reason: FormControl<string>;
  campaignCode: FormControl<string>;
  reasonDescription: FormControl<string>;
  signature: FormControl<string>;
  sales: FormControl<{ id: string, name: string, email: string }>;
  calculator: FormControl<{ id: string, name: string, email: string }>;
  contacts: FormControl<string[]>;
  transactionId: FormControl<string>;
  productionMode: FormControl<ProductionModeEnum | null>;
  productionPrice: FormControl<number>;
  deliveryPrice: FormControl<number | null>;
  deliveryPoints: FormControl<number>;
  deliveryDate: FormControl<Date>;
}>

export class QuotationDelivery {
  id: string;
  address: DeliveryAddress;
  products: { poId: string, quantity: number }[]
}

export class Quotation {

  id: string;
  description: string;
  email: string;
  contactName: string;
  reference: string;
  company: string;
  state: QuotationState;
  customerId: string;
  date: Date;
  emailSent: Notification;
  reason: string;
  reasonDescription: string;
  signature: string;
  sales: ({ id: string, name: string, email: string });
  calculator: ({ id: string, name: string, email: string });
  lockComment: string;
  lockedBy = 'none';
  lockerName: string;
  lockDate: Date;
  validationDate: Date;
  documents: DocumentModel[];
  source: SourceType;
  erpId: string;
  organizationId: string;
  version: number;
  products: ProductOrder[];
  contacts: string[];
  transactionId: string;
  recallDate: Date;
  campaignCode: string;
  productionMode: ProductionModeEnum | null;
  productionPrice: number;
  deliveryPrice: number | null;
  deliveryPoints: number;
  deliveryDate: Date | null;
  /** Represents a collection of delivery addresses.*/
  deliveries: QuotationDelivery[];

  constructor(quotation?: Partial<Quotation>) {
    this.id = quotation?.id ?? '';
    this.description = quotation?.description ?? '';
    this.email = quotation?.email ?? '';
    this.contactName = quotation?.contactName ?? '';
    this.reference = quotation?.reference ?? '';
    this.company = quotation?.company ?? '';
    this.state = quotation?.state ?? QuotationState.NEW;
    this.customerId = quotation?.customerId ?? '';
    this.date = quotation?.date ? quotation.date : null;
    this.emailSent = quotation?.emailSent ?? Notification.NONE_SENT;
    this.reason = quotation?.reason ?? '';
    this.reasonDescription = quotation?.reasonDescription ?? '';
    this.signature = quotation?.signature ?? '';
    this.lockComment = quotation?.lockComment ?? '';
    this.lockedBy = quotation?.lockedBy ?? 'none';
    this.lockerName = quotation?.lockerName ?? '';
    this.lockDate = quotation?.lockDate ?? null;
    this.validationDate = quotation?.validationDate ?? null;
    this.documents = quotation?.documents ?? null;
    this.campaignCode = quotation?.campaignCode ?? '';
    this.sales = {
      id: quotation?.sales?.id ?? '',
      name: quotation?.sales?.name ?? '',
      email: quotation?.sales?.email ?? '',
    };
    this.calculator = {
      id: quotation?.calculator?.id ?? '',
      name: quotation?.calculator?.name ?? '',
      email: quotation?.calculator?.email ?? '',
    };
    this.source = quotation?.source ?? SourceType.OKTO;
    this.erpId = quotation?.erpId ?? '';
    this.organizationId = quotation?.organizationId ?? '';
    this.version = quotation?.version ?? 1;
    this.products = (quotation?.products ?? []).map(product => new ProductOrder(product));
    this.contacts = quotation?.contacts ?? [];
    this.transactionId = quotation?.transactionId ?? '';
    this.recallDate = quotation?.recallDate ? quotation.recallDate : null;
    this.productionMode = quotation?.productionMode ? quotation.productionMode: ProductionModeEnum.STANDARD;
    this.productionPrice = quotation?.productionPrice ?? 0;
    this.deliveryPrice = quotation?.deliveryPrice ?? null;
    this.deliveryPoints = quotation?.deliveryPoints ? quotation.deliveryPoints : 1;
    this.deliveryDate = quotation?.deliveryDate ?? null;
    this.deliveries = quotation?.deliveries ?? [];
  }

  /**
   * Converter used to convert a user from firebase
   */
  static converter: FirestoreDataConverter<Quotation> = {
    toFirestore: (quotation: Quotation) => ({
      id: quotation.id,
      description: quotation.description,
      email: quotation.email,
      contactName: quotation.contactName,
      reference: quotation.reference,
      company: quotation.company,
      state: quotation.state,
      customerId: quotation.customerId,
      date: quotation.date,
      reason: quotation.reason,
      reasonDescription: quotation.reasonDescription,
      signature: quotation.signature,
      emailSent: quotation.emailSent,
      sales: quotation.sales,
      calculator: quotation.calculator,
      lockComment: quotation.lockComment,
      lockedBy: quotation.lockedBy,
      lockerName: quotation.lockerName,
      lockDate: quotation.lockDate,
      validationDate: quotation.validationDate,
      documents: quotation.documents,
      campaignCode: quotation.campaignCode,
      source: quotation.source,
      erpId: quotation.erpId,
      organizationId: quotation.organizationId,
      version: quotation.version,
      products: ObjectHelper.anonymize(quotation.products),
      contacts: quotation.contacts,
      transactionId: quotation.transactionId,
      recallDate: quotation.recallDate,
      productionMode: quotation.productionMode,
      productionPrice: quotation.productionPrice,
      deliveryPrice: quotation.deliveryPrice,
      deliveryPoints: quotation.deliveryPoints,
      deliveryDate: quotation.deliveryDate,
      deliveries: quotation.deliveries,
    }),
    fromFirestore: (snapshot, options) => {
      const data = snapshot.data(options);
      return new Quotation({
        ...data,
        id: snapshot.id,
        date: data.date ? new Date(data.date.seconds * 1000) : null,
        recallDate: data.recallDate ? new Date(data.recallDate.seconds * 1000) : null,
        validationDate: data.validationDate ? new Date(data.validationDate.seconds * 1000) : null,
        lockDate: data.lockDate ? new Date(data.lockDate.seconds * 1000) : null,
        deliveryDate: data.deliveryDate ? new Date(data.deliveryDate.seconds * 1000) : null,
        products: (data.products ?? []).map((product: any) => ({
          ...product,
          deliveryDate: product.deliveryDate ? new Date(product.deliveryDate.seconds * 1000) : null
        })),
      });
    }
  }
}
